(() => {
  const initLibrary = (root) => {
    if (!root || root.dataset.aquaVideosReady === 'true') return;
    root.dataset.aquaVideosReady = 'true';
    document.body.classList.add('aqua-videos-page');

    const controller = new AbortController();
    const listenerOptions = { signal: controller.signal };
    const navigation = root.querySelector('[data-aqua-videos-navigation]');
    const hero = document.querySelector('.aqua-videos-hero');
    const links = [...root.querySelectorAll('[data-aqua-videos-anchor]')];
    const groups = links.map((link) => root.querySelector(link.getAttribute('href'))).filter(Boolean);
    const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let activeId = null;
    let programmaticTargetId = null;
    let scrollSettleTimer = null;
    let ticking = false;

    const headerOffset = () => {
      const candidates = document.querySelectorAll('sticky-header, .shopify-section-header-sticky, .header-wrapper');
      let height = 0;
      candidates.forEach((header) => {
        const styles = window.getComputedStyle(header);
        if (styles.position === 'fixed' || styles.position === 'sticky') {
          height = Math.max(height, header.getBoundingClientRect().height);
        }
      });
      return height + 24;
    };

    const setActive = (target) => {
      if (!target || activeId === target.id) return;
      activeId = target.id;
      links.forEach((link) => {
        const isActive = link.getAttribute('href') === `#${activeId}`;
        link.classList.toggle('is-active', isActive);
        if (isActive) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
    };

    const updateNavigationHeight = () => {
      if (!navigation) return;
      document.body.style.setProperty(
        '--aqua-videos-navigation-height',
        `${Math.ceil(navigation.getBoundingClientRect().height)}px`
      );
    };

    const updateVisibility = () => {
      if (!navigation) return;
      const shouldShow = hero ? hero.getBoundingClientRect().bottom <= headerOffset() : window.scrollY > 0;
      if (navigation.classList.contains('is-visible') === shouldShow) return;
      navigation.classList.toggle('is-visible', shouldShow);
      document.body.classList.toggle('aqua-videos-navigation-visible', shouldShow);
      if (shouldShow) updateNavigationHeight();
    };

    const updateActiveFromScroll = () => {
      if (!groups.length || programmaticTargetId) return;
      const marker = headerOffset() + 48;
      let current = groups[0];
      groups.forEach((group) => {
        if (group.getBoundingClientRect().top <= marker) current = group;
      });
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4) {
        current = groups[groups.length - 1];
      }
      setActive(current);
    };

    const updateViewportState = () => {
      ticking = false;
      updateVisibility();
      updateActiveFromScroll();
    };

    const requestUpdate = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(updateViewportState);
    };

    const releaseProgrammaticScroll = () => {
      if (!programmaticTargetId) return;
      programmaticTargetId = null;
      window.clearTimeout(scrollSettleTimer);
      scrollSettleTimer = null;
      requestUpdate();
    };

    const noteProgrammaticScrollProgress = () => {
      if (!programmaticTargetId) return;
      window.clearTimeout(scrollSettleTimer);
      scrollSettleTimer = window.setTimeout(releaseProgrammaticScroll, 180);
    };

    links.forEach((link) => {
      link.addEventListener('click', (event) => {
        const target = root.querySelector(link.getAttribute('href'));
        if (!target) return;
        event.preventDefault();
        window.clearTimeout(scrollSettleTimer);
        programmaticTargetId = target.id;
        setActive(target);
        window.scrollTo({
          top: target.getBoundingClientRect().top + window.scrollY - headerOffset(),
          behavior: reducedMotion() ? 'auto' : 'smooth'
        });
        if (history.replaceState) {
          const url = new URL(window.location.href);
          url.hash = link.getAttribute('href');
          history.replaceState(null, '', url);
        }
        if (reducedMotion()) window.requestAnimationFrame(releaseProgrammaticScroll);
      }, listenerOptions);
    });

    root.querySelector('[data-aqua-videos-top]')?.addEventListener('click', () => {
      releaseProgrammaticScroll();
      window.scrollTo({ top: 0, behavior: reducedMotion() ? 'auto' : 'smooth' });
    }, listenerOptions);

    const interruptProgrammaticScroll = () => releaseProgrammaticScroll();
    const interruptWithKey = (event) => {
      if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key)) {
        interruptProgrammaticScroll();
      }
    };

    window.addEventListener('scroll', () => {
      noteProgrammaticScrollProgress();
      requestUpdate();
    }, { passive: true, signal: controller.signal });
    window.addEventListener('resize', () => {
      requestUpdate();
      updateNavigationHeight();
    }, { passive: true, signal: controller.signal });
    window.addEventListener('wheel', interruptProgrammaticScroll, { passive: true, signal: controller.signal });
    window.addEventListener('touchstart', interruptProgrammaticScroll, { passive: true, signal: controller.signal });
    window.addEventListener('keydown', interruptWithKey, listenerOptions);
    window.addEventListener('hashchange', requestUpdate, listenerOptions);
    window.addEventListener('pageshow', requestUpdate, listenerOptions);
    if ('onscrollend' in window) window.addEventListener('scrollend', releaseProgrammaticScroll, listenerOptions);

    const navigationObserver = typeof ResizeObserver === 'undefined' || !navigation
      ? null
      : new ResizeObserver(updateNavigationHeight);
    navigationObserver?.observe(navigation);

    root.aquaVideosCleanup = () => {
      navigationObserver?.disconnect();
      controller.abort();
      window.clearTimeout(scrollSettleTimer);
      document.body.classList.remove('aqua-videos-navigation-visible');
      document.body.style.removeProperty('--aqua-videos-navigation-height');
    };

    const initialHashTarget = groups.find((group) => `#${group.id}` === window.location.hash);
    if (initialHashTarget) {
      programmaticTargetId = initialHashTarget.id;
      setActive(initialHashTarget);
      scrollSettleTimer = window.setTimeout(releaseProgrammaticScroll, 250);
    }

    updateNavigationHeight();
    updateViewportState();
    window.requestAnimationFrame(updateViewportState);
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-aqua-videos-library]').forEach(initLibrary);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector('[data-aqua-videos-library]');
    root?.aquaVideosCleanup?.();
    window.setTimeout(() => {
      if (!document.querySelector('[data-aqua-videos-library]')) {
        document.body.classList.remove('aqua-videos-page');
      }
    }, 0);
  });
})();
